import { AppHeader } from '@/components/app-header';
import { AppSidebar } from '@/components/app-sidebar';
import { ProtectedRoute } from '@/components/protected-route';
import { ForbiddenPage, RoleRoute } from '@/components/role-route';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AuthProvider, useAuth } from '@/contexts/auth-context';
import { EditActivityPage } from '@/pages/edit-activity';
import { EditGroupPage } from '@/pages/edit-group';
import { EditInsurancePage } from '@/pages/edit-insurance';
import { EditProjectPage } from '@/pages/edit-project';
import { FailedSubmissionDetailPage } from '@/pages/failed-submission-detail';
import { FailedSubmissionsPage } from '@/pages/failed-submissions';
import { FormProvidersPage } from '@/pages/form-providers';
import { GroupDetailPage } from '@/pages/group-detail';
import { GroupsPage } from '@/pages/groups';
import LoginPage from '@/pages/login';
import { LogsPage } from '@/pages/logs';
import { NewActivityPage } from '@/pages/new-activity';
import { NewGroupPage } from '@/pages/new-group';
import { NewInsurancePage } from '@/pages/new-insurance';
import { NewProjectPage } from '@/pages/new-project';
import { ProjectDetailPage } from '@/pages/project-detail';
import { ProjectsPage } from '@/pages/projects';
import { RegistrationDetailPage } from '@/pages/registration-detail';
import { SettingsPage } from '@/pages/settings';
import { CasesPage } from '@/pages/cases';
import { NewCasePage } from '@/pages/new-case';
import { CaseEditorPage } from '@/pages/case-editor';
import { BlogArticleEditorPage, BlogArticlesPage } from '@/pages/blog';
import { UsersPage } from '@/pages/users';
import { ContentMigrationPage } from '@/pages/content-migration';
import { defaultBackofficePath } from '@fame/shared';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { Toaster } from 'sonner';

/**
 * Layout rendered for all authenticated routes.
 * `<Outlet />` is where the matched child route renders.
 * Using a layout route instead of a nested <Routes> avoids the React Router
 * v6 anti-pattern of rendering <Routes> inside a wildcard route's element.
 */
function AppLayout() {
  return (
    <ProtectedRoute>
      <RoleRoute>
        <SidebarProvider>
          <div className="flex min-h-screen w-full">
            <AppSidebar />
            <div className="flex flex-1 flex-col">
              <AppHeader />
              <main className="flex-1 p-6">
                <Outlet />
              </main>
            </div>
          </div>
        </SidebarProvider>
      </RoleRoute>
    </ProtectedRoute>
  );
}

function DefaultRoute() {
  const { user } = useAuth();
  return <Navigate to={defaultBackofficePath(user?.roles ?? [])} replace />;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <AuthProvider>
          <Outlet />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      }
    >
      {/* Public route */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/verboden"
        element={
          <ProtectedRoute>
            <ForbiddenPage />
          </ProtectedRoute>
        }
      />

      {/* Protected layout — all child routes render inside AppLayout via <Outlet /> */}
      <Route element={<AppLayout />}>
        <Route index element={<DefaultRoute />} />
        <Route path="/projecten" element={<ProjectsPage />} />
        <Route path="/projecten/nieuw" element={<NewProjectPage />} />
        <Route path="/projecten/:id" element={<ProjectDetailPage />} />
        <Route path="/projecten/:id/bewerken" element={<EditProjectPage />} />
        <Route path="/projecten/:projectId/groepen/nieuw" element={<NewGroupPage />} />
        <Route path="/projecten/:projectId/activiteiten/nieuw" element={<NewActivityPage />} />
        <Route path="/projecten/:projectId/verzekeringen/nieuw" element={<NewInsurancePage />} />
        <Route path="/groepen" element={<GroupsPage />} />
        <Route path="/groepen/:id" element={<GroupDetailPage />} />
        <Route path="/groepen/:id/bewerken" element={<EditGroupPage />} />
        <Route path="/groepen/:groupId/registraties/:id" element={<RegistrationDetailPage />} />
        <Route path="/activiteiten/:id/bewerken" element={<EditActivityPage />} />
        <Route path="/verzekeringen/:id/bewerken" element={<EditInsurancePage />} />
        <Route path="/formulier-providers" element={<FormProvidersPage />} />
        <Route path="/mislukte-inzendingen" element={<FailedSubmissionsPage />} />
        <Route path="/mislukte-inzendingen/:id" element={<FailedSubmissionDetailPage />} />
        <Route path="/registraties/instellingen" element={<SettingsPage section="registrations" />} />
        <Route path="/instellingen" element={<SettingsPage />} />
        <Route path="/gebruikers" element={<UsersPage />} />
        <Route path="/logs" element={<LogsPage />} />
        <Route path="/cases" element={<CasesPage />} />
        <Route path="/cases/nieuw" element={<NewCasePage />} />
        <Route path="/cases/:id/bewerken" element={<CaseEditorPage />} />
        <Route path="/blog" element={<BlogArticlesPage />} />
        <Route path="/website/instellingen" element={<SettingsPage section="website" />} />
        <Route path="/migration" element={<ContentMigrationPage />} />
        <Route path="/blog/nieuw" element={<BlogArticleEditorPage />} />
        <Route path="/blog/:id/bewerken" element={<BlogArticleEditorPage />} />
      </Route>
    </Route>
  )
);

export default function App() {
  return <RouterProvider router={router} />;
}
